import { Address, Store } from "@/actions/schemas/stores";
import {
  DeliveryMethods,
  type Item,
  type Summary,
} from "@/stores/order-resume-store";
import { ChosenVariants } from "@/stores/product-store";
import { formatAddress } from "@/utils/formatters";
import { Mask } from "@/utils/masks";

const SEPARATOR = "---";

type Customer = {
  name: string;
  address: Omit<Address, "showMapPreview"> | null;
};

type OrderResumeMessageParams = {
  items: Item[];
  summary: Summary;
  store?: Store;
  customer?: Customer;
  deliveryMethod?: DeliveryMethods;
};

type ProductMessageParams = {
  productName: string;
  price: number;
  chosenVariants: ChosenVariants;
  link: string;
};

const buildItemsSection = (items: Item[]): string => {
  const itemLines = items.map(
    (item) => `- *${item.quantity}x ${item.name}*\n  - ${item.description}`
  );
  return `${itemLines.join("\n")}`;
};

const buildDeliveryPriceSection = (store?: Store): string => {
  if (!store) return "";

  const { hasDeliveryCharge, deliveryPrice, hasNegotiableDelivery } =
    store.vitrine.preCheckout.delivery;

  if (!hasDeliveryCharge) return "";

  if (hasNegotiableDelivery) return "- Entrega: A combinar";

  if (deliveryPrice === 0) return "- Entrega: Grátis";

  return `- Taxa de entrega: ${Mask.formatToBrlCurrency(deliveryPrice)}`;
};

const buildDeliverySummarySection = (
  summary: Summary,
  store: Store
): string => {
  const lines = [
    `- ${summary.itemsCount} ${summary.itemsCount === 1 ? "item" : "itens"}: ${Mask.formatToBrlCurrency(summary.subtotal)}`,
    buildDeliveryPriceSection(store),
    `- Total: *${Mask.formatToBrlCurrency(summary.total)}*`,
  ];
  return lines.join("\n");
};

const buildSummarySection = (summary: Summary): string => {
  const lines = [
    `- ${summary.itemsCount} ${summary.itemsCount === 1 ? "item" : "itens"}: ${Mask.formatToBrlCurrency(summary.subtotal)}`,
    `- Total: *${Mask.formatToBrlCurrency(summary.total)}*`,
  ];
  return lines.join("\n");
};

const buildStoreAddressSection = (store?: Store): string => {
  if (!store) return "";

  const address = formatAddress(store.vitrine.address!)?.complete || "";
  return `*Retirada na loja* \n ${address}`;
};

const buildCustomerAddressSection = (customer: Customer): string => {
  if (!customer) return "";

  const address = formatAddress(customer.address!)?.complete || "";
  return `*Entrega em domicílio* \n ${address}`;
};

const buildVariantsSections = (chosenVariants: ChosenVariants): string => {
  return Object.values(chosenVariants)
    .map((variant) => `${variant.type}: ${variant.name}`)
    .join("\n");
};

const buildOrderResumeMessage = ({
  items,
  summary,
  store,
  customer,
  deliveryMethod,
}: OrderResumeMessageParams): string => {
  const method =
    deliveryMethod === DeliveryMethods.PICKUP
      ? buildOrderResumePickupMessage
      : deliveryMethod === DeliveryMethods.DELIVERY
        ? buildOrderResumeDeliveryMessage
        : buildOrderResumeSimpleMessage;

  return method({
    items,
    summary,
    store,
    customer,
  });
};

const buildOrderResumeSimpleMessage = ({
  items,
  summary,
}: OrderResumeMessageParams): string => {
  const sections = [
    `_Olá! Quero fazer um pedido:_\n`,
    buildItemsSection(items),
    buildSummarySection(summary),
    `\n_Aguarde a resposta da loja_`,
  ];

  return sections.join(`\n${SEPARATOR}\n`);
};

const buildOrderResumePickupMessage = ({
  items,
  summary,
  store,
  customer,
}: OrderResumeMessageParams): string => {
  const sections = [
    `_Olá! Quero fazer um pedido:_\n`,
    buildItemsSection(items),
    buildSummarySection(summary),
    customer!.name,
    buildStoreAddressSection(store),
    `\n_Aguarde a resposta da loja_`,
  ];

  return sections.join(`\n${SEPARATOR}\n`);
};

const buildOrderResumeDeliveryMessage = ({
  items,
  summary,
  store,
  customer,
}: OrderResumeMessageParams): string => {
  const sections = [
    `_Olá! Quero fazer um pedido:_\n`,
    buildItemsSection(items),
    buildDeliverySummarySection(summary, store!),
    customer!.name,
    buildCustomerAddressSection(customer!),
    `\n_Aguarde a resposta da loja_`,
  ];

  return sections.join(`\n${SEPARATOR}\n`);
};

const buildProductMessage = ({
  productName,
  price,
  chosenVariants,
  link,
}: ProductMessageParams): string => {
  const sections = [
    `Olá! Quero pedir este produto:\n`,
    `*${productName}*`,
    buildVariantsSections(chosenVariants),
    ` ---`,
    `*Subtotal: ${Mask.formatToBrlCurrency(price)}*`,
    ` ---`,
    link,
  ];

  return sections.join(`\n`);
};

const buildCatalogGreeting = (
  storeName: string,
  experiment: { key?: string } | undefined
): string =>
  experiment?.key === "on"
    ? `Olá! Acessei o catálogo da loja ${storeName} e gostaria de mais informações.`
    : `Olá! Acessei o catálogo da ${storeName} e quero fazer um pedido.`;

export const MessageBuilder = {
  buildOrderResumeMessage,
  buildProductMessage,
  buildCatalogGreeting,
};
